import { useRouter } from 'next/router'
import { Fragment, memo, useCallback } from 'react'

import { ImageWithPlaceholder } from '@components/ImageWithPlaceholder'
import { useIsLaptop, useIsMobileXL } from '@hooks/use-media-query'
import { NormalizedWrapper } from '@theme'
import { Description } from '@ui/sections/Description'

import {
  BlockWrapper,
  LinkWrapper,
  Content,
  ContentWrapper,
  HexagonImage,
  ImageWrapper,
  Subtitle,
  TextContent,
  Title,
  TitleWrapper,
  ExperienceSectionHorizontalHeader,
} from './styles'

function ExperienceSectionComponent({
  config,
  title,
  subtitle,
  oneRow,
  padding,
  hexagon,
  as = 'h3',
  hover = false,
  uplift = false,
  rowItems,
  horizontalHeader = false,
  textTransform = '',
  textAlign = '',
  titlePosition = '',
}) {
  const router = useRouter()
  const isLaptop = useIsLaptop()
  const isMobile = useIsMobileXL()

  const handleClick = value => () => {
    router.push(value)
  }

  const HeaderWrapper = horizontalHeader
    ? ExperienceSectionHorizontalHeader
    : Fragment

  const contentValue = useCallback(
    ({
      id,
      title: contentTitle,
      laptopTitle,
      subtitle: contentSubTitle,
      text,
      imagePath,
      imageHeight,
      imageWidth,
      imageHeightMobile = 65,
      imageWidthMobile = 65,
      alt,
      additional,
      imageTitle,
      titleLink,
      borderColor,
      titleHeight = 'auto',
      topMarginLink,
    }) => {
      const isFragment = HeaderWrapper === Fragment
      return (
        <Content
          key={`${alt}_${id}`}
          className="container"
          hover={hover}
          uplift={uplift}
        >
          <BlockWrapper className="content" $oneRow={oneRow}>
            <HeaderWrapper
              {...(!isFragment ? { className: 'header-wrapper' } : {})}
            >
              {imagePath === '/' || (
                <ImageWrapper
                  $borderColor={borderColor}
                  width={
                    isMobile && horizontalHeader ? imageWidthMobile : imageWidth
                  }
                  height={
                    isMobile && horizontalHeader
                      ? imageHeightMobile
                      : imageHeight
                  }
                  $titleLink={titleLink}
                  className="image-block"
                >
                  <ImageWithPlaceholder
                    src={imagePath}
                    alt={alt}
                    title={imageTitle}
                    width={
                      isMobile && horizontalHeader
                        ? imageWidthMobile
                        : imageWidth
                    }
                    height={
                      isMobile && horizontalHeader
                        ? imageHeightMobile
                        : imageHeight
                    }
                    onClick={titleLink && handleClick(titleLink)}
                  />
                </ImageWrapper>
              )}
              {titleLink ? (
                <TitleWrapper
                  titleHeight={titleHeight}
                  topMarginLink={topMarginLink}
                  className="title-wrapper"
                >
                  <Title
                    href={titleLink}
                    $oneRow={oneRow}
                    className="title"
                    $titleLink
                  >
                    {contentTitle}
                  </Title>
                </TitleWrapper>
              ) : (
                <Title as={as} className="title" $oneRow={oneRow}>
                  {!isLaptop && laptopTitle ? laptopTitle : contentTitle}
                </Title>
              )}
            </HeaderWrapper>
            {contentSubTitle && (
              <TextContent className="text">{contentSubTitle}</TextContent>
            )}
          </BlockWrapper>
          {text && (
            <Subtitle className="subtitle" oneRow={oneRow}>
              {text}
            </Subtitle>
          )}
          {additional && (
            <LinkWrapper href={titleLink}>{additional}</LinkWrapper>
          )}
        </Content>
      )
    },
    [config, horizontalHeader, oneRow, hover, uplift]
  )

  return (
    <>
      {hexagon && (
        <HexagonImage className="additional-hexagon">{hexagon}</HexagonImage>
      )}
      <NormalizedWrapper className="wrapper" zIndex="2">
        <Description
          textTransform={textTransform}
          textPosition={textAlign}
          titlePosition={titlePosition}
          title={title}
          subtitle={subtitle}
          padding={padding}
        />
        <ContentWrapper className="content-wrapper" rowItems={rowItems}>
          {config.map(contentValue)}
        </ContentWrapper>
      </NormalizedWrapper>
    </>
  )
}

export const ExperienceSection = memo(ExperienceSectionComponent)
